import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '../services/api';
import { taskKeys } from '../lib/queryKeys';
import { PRIORITIES } from '../lib/constants';
import { statusName } from '../lib/statusName';
import { useDateTime } from '../contexts/DateTimeContext';
import { useLabelsQuery, useMyBotsQuery, useProjectSectionsQuery, useProjectsQuery, useTeamsQuery } from '../hooks/queries';
import { useAuth } from '../contexts/AuthContext';
import { botsApi } from '../services/api';
import { botOf, botsFor, uniqueLabels } from './create/taskFromParse';
import BotAvatar from './bots/BotAvatar';
import { dayWords, parseQuickInput } from './create/quickParse';
import { peopleOf, useTokenSuggest } from './create/useTokenSuggest';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import DatePicker, { dayOf } from './task/pickers/DatePicker';

/**
 * InlineTaskRow - Quick inline task creation at the bottom of a task list.
 *
 * Two layout modes:
 *  • Compact (default): a thin "+ Add task" button that opens a single-line
 *    text input. Used by KanbanBoard columns and other dense surfaces.
 *  • Table grid (when `gridTemplate` is provided): renders as a row matching
 *    the surrounding TasksTable layout - same height, font, and column
 *    structure - with click-to-edit chips for status / priority / due date /
 *    duration / project. The user can adjust any value BEFORE pressing Enter,
 *    matching ClickUp's quick-add UX.
 *
 * Inline title hints (parsed before submit):
 *   !p1-!p4    → set priority
 *   @today     → due today
 *   @tomorrow  → due tomorrow
 *   ~30m ~2h   → set duration
 */
const InlineTaskRow = ({
  // Common
  projectId = null,
  sectionId = null,
  statusId = null,
  teamId = null,
  dueDate = null,
  // Set: the new task is a subtask of this one (and lands in its project and list).
  parentId = null,
  onTaskCreated,
  className = '',
  placeholder = null,
  // Table-grid layout (passed from TasksTable). When all three are present,
  // render as a grid row matching the surrounding tasks.
  gridTemplate = null,
  columnOrder = null,
  visibleColumns = null,
  customStatuses = [],
  projects = [],
  labels: availableLabels = [],
  taskTypes = [],
  // True when the parent group is rendered with an offset (groupBy != 'none')
  // so this row aligns with the indented task rows above it.
  indented = false,
  // True when the surrounding table is in bulk-select mode, so the grid has a
  // leading 40px checkbox column. We render an empty spacer cell to keep the
  // quick-add fields aligned with the task rows above.
  leadingCell = false,
}) => {
  const { t, i18n } = useTranslation();
  const { toLocalDateString, toUTCISO, formatDate } = useDateTime();
  const qc = useQueryClient();
  const resolvedPlaceholder = placeholder ?? t('tasks.add_task');
  const [isActive, setIsActive] = useState(false);
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const isTableLayout = !!(gridTemplate && Array.isArray(columnOrder) && visibleColumns);

  // Default values from props - the user can override any of these via the
  // chip popovers before pressing Enter. Recomputed when the prop defaults
  // change (e.g. switching to a different day in the agenda view).
  const defaultStatus = useMemo(() => {
    if (!Array.isArray(customStatuses) || customStatuses.length === 0) return null;
    const target = statusId
      ? customStatuses.find((s) => s.id === statusId)
      : customStatuses.find((s) => s.category === 'not_started');
    return target || customStatuses[0];
  }, [customStatuses, statusId]);

  const defaultDueDateStr = dueDate
    ? toLocalDateString(dueDate instanceof Date ? dueDate : new Date(dueDate))
    : null;

  // Per-row form state. Reset when the prop defaults change (e.g. a new
  // day's agenda block) AND when the row goes inactive.
  const [chosenPriority, setChosenPriority] = useState('p4');
  const [chosenStatusId, setChosenStatusId] = useState(null);
  const [chosenDueDate, setChosenDueDate] = useState(null); // YYYY-MM-DD or null
  const [chosenDueTime, setChosenDueTime] = useState('');   // HH:MM from the picker, or ''
  const [chosenDuration, setChosenDuration] = useState(60);
  const [chosenProjectId, setChosenProjectId] = useState(null);
  const [chosenLabelIds, setChosenLabelIds] = useState([]);
  const [chosenTaskTypeId, setChosenTaskTypeId] = useState(null);

  useEffect(() => {
    if (isActive) return;
    setChosenPriority('p4');
    setChosenStatusId(defaultStatus?.id || null);
    setChosenDueDate(defaultDueDateStr);
    setChosenDueTime('');
    setChosenDuration(60);
    setChosenProjectId(projectId || null);
    setChosenLabelIds([]);
    setChosenTaskTypeId(null);
  }, [isActive, defaultStatus, defaultDueDateStr, projectId]);

  // Insert the freshly-created task into every cached tasks list so the UI
  // updates instantly without waiting for the background refetch.
  const optimisticInsert = (task) => {
    const lists = qc.getQueriesData({ queryKey: taskKeys.lists() });
    lists.forEach(([key, list]) => {
      if (!Array.isArray(list)) return;
      if (list.some((t) => t.id === task.id)) return;
      qc.setQueryData(key, [...list, task]);
    });
  };

  // Focus when activated, and re-focus when a creation cycle finishes - the
  // input is `disabled={creating}` during the POST so any focus() call inside
  // handleCreate hits a disabled element and is a no-op. Running the focus
  // here, after `creating` flips back to false, lands it on the now-enabled
  // input ready for the next task.
  useEffect(() => {
    if (
      isActive &&
      !creating &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      inputRef.current.focus();
    }
  }, [isActive, creating]);

  // ── What the typed line says ───────────────────────────────────────────────
  // The same reading as every quick input (components/create/quickParse.js): "#project",
  // "@person" (the target project's team), a day or a time in the app's language, p1..p4,
  // 30m; the older "!p1", "~30m", "@today" still read. "#" and "@" suggest as you type.
  const { data: allProjectsData } = useProjectsQuery();
  const { data: teams } = useTeamsQuery();
  const allProjects = useMemo(() => (allProjectsData || projects || []).filter((p) => !p.deleted_at), [allProjectsData, projects]);
  const words = useMemo(() => dayWords(i18n.language, t), [i18n.language, t]);
  const { user } = useAuth();
  const { data: labelsData } = useLabelsQuery();
  const { data: mine } = useMyBotsQuery();
  const home = allProjects.find((p) => p.id === (chosenProjectId || projectId)) || null;
  const target = useMemo(() => parseQuickInput(title, { projects: allProjects, words }).project || home,
    [title, allProjects, words, home]);
  const { data: sectionsData } = useProjectSectionsQuery(target?.id);
  const read = useCallback((text) => {
    const tp = parseQuickInput(text, { projects: allProjects, words }).project || home;
    const people = [...peopleOf(tp, teams), ...botsFor(tp, mine, user?.id)];
    const sections = tp && tp.id === target?.id && Array.isArray(sectionsData) ? sectionsData.filter((x) => !x.deleted_at) : [];
    const labels = uniqueLabels(Array.isArray(labelsData) ? labelsData : [], user?.id);
    return { ...parseQuickInput(text, { projects: allProjects, people, sections, labels, words }), people,
      sectionOptions: sections, labelOptions: labels };
  }, [allProjects, words, teams, mine, user, home, target, sectionsData, labelsData]);
  const parsed = useMemo(() => read(title), [read, title]);
  const suggest = useTokenSuggest({ value: title, setValue: setTitle, inputRef, projects: allProjects, teams,
    people: parsed.people, sections: parsed.sectionOptions, labels: parsed.labelOptions });

  const buildTaskData = (rawTitle) => {
    const line = read(rawTitle);
    const finalDueDate = line.date ? toLocalDateString(line.date) : chosenDueDate;
    const finalProjectId = line.project?.id || chosenProjectId || projectId || null;
    // A "#other project" leaves this list, its status and its team behind.
    const here = finalProjectId === (projectId || null);
    const finalStatusId = chosenStatusId || statusId || null;
    const data = {
      title: line.title || rawTitle,
      priority: line.priority || chosenPriority,
      duration: line.duration || chosenDuration,
      ...(finalDueDate && { due_date: finalDueDate }),
      ...(!line.date && chosenDueDate && chosenDueTime && { due_time: chosenDueTime }),
      ...(finalProjectId && { project_id: finalProjectId }),
      ...(line.section ? { section_id: line.section.id } : sectionId && here && { section_id: sectionId }),
      ...(parentId && { parent_id: parentId }),
      ...(finalStatusId && here && { status_id: finalStatusId }),
      ...(teamId && here && { team_id: teamId }),
      ...(line.person?.user_id && { assigned_to: [line.person.user_id] }),
      ...(line.recurrence && { recurrence: line.recurrence }),
      ...((chosenLabelIds.length > 0 || line.labels.length > 0) && {
        label_ids: [...new Set([...chosenLabelIds, ...line.labels.map((l) => l.id)])] }),
      ...(chosenTaskTypeId && { task_type_id: chosenTaskTypeId }),
    };
    if (line.time && line.date) {
      // A time puts it in the calendar, as the other quick inputs do (unless that time is gone).
      data.due_time = line.time;
      const start = new Date(line.date);
      const [h, m] = line.time.split(':').map(Number);
      start.setHours(h, m, 0, 0);
      if (start > new Date()) {
        data.scheduled_start = toUTCISO(start);
        data.scheduled_end = toUTCISO(new Date(start.getTime() + (data.duration || 60) * 60000));
      }
    }
    return data;
  };

  const liveChip = 'inline-flex items-center gap-1 h-5 px-1.5 rounded-full border border-border text-[11px] text-foreground/80 min-w-0 max-w-[160px]';
  // In the table the row's own cells show the project, day, priority and length; the title
  // cell adds only who it goes to (and the project when its column is hidden).
  const personInTitle = parsed.person && visibleColumns?.assignee === false;
  const projectInTitle = parsed.project && visibleColumns?.project === false;
  const gridChips = (personInTitle || projectInTitle) ? (
    <span className="flex items-center gap-1 min-w-0 max-w-[50%] overflow-hidden" data-testid="inline-task-chips">
      {projectInTitle && (
        <span className={liveChip}>
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: parsed.project.color || '#3B82F6' }} />
          <span className="truncate">{parsed.project.name}</span>
        </span>
      )}
      {personInTitle && <span className={liveChip}><span className="truncate">@{parsed.personToken}</span></span>}
    </span>
  ) : null;
  // The day as the other quick inputs name it (the parsed day is a local date, not an instant).
  const dayLabel = (d) => {
    const ahead = Math.round((new Date(d).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (ahead === 0) return t('common.today');
    if (ahead === 1) return t('common.tomorrow');
    return d.toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' });
  };
  // What the line will set, shown as it is typed (as the desktop panel does).
  const liveChips = (parsed.project || parsed.person || parsed.date || parsed.priority) ? (
    <span className="flex items-center gap-1 flex-shrink min-w-0 overflow-hidden" data-testid="inline-task-chips">
      {parsed.project && (
        <span className={liveChip}>
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: parsed.project.color || '#3B82F6' }} />
          <span className="truncate">{parsed.project.name}</span>
        </span>
      )}
      {parsed.person && <span className={liveChip}><span className="truncate">@{parsed.personToken}</span></span>}
      {parsed.date && <span className={liveChip}>{dayLabel(parsed.date)}{parsed.time ? ` ${parsed.time}` : ''}</span>}
      {parsed.priority && <span className={liveChip}>{parsed.priority.toUpperCase()}</span>}
    </span>
  ) : null;

  const handleCreate = async ({ keepActive = true } = {}) => {
    const trimmed = title.trim();
    if (!trimmed || creating) return;

    setCreating(true);
    try {
      const taskData = buildTaskData(trimmed);
      const res = await tasksApi.create(taskData);
      const newTask = res.data;
      const bot = botOf(read(trimmed));
      if (bot && newTask?.id) await botsApi.assignTask(newTask.id, bot);
      optimisticInsert(newTask);
      onTaskCreated?.(newTask);
      // Enter → keep active for rapid entry. Click-outside / blur → finish
      // and collapse the row back to the inactive "+ Add task" placeholder.
      setTitle('');
      if (!keepActive) setIsActive(false);
    } catch (err) {
      console.error('Failed to create task:', err);
    } finally {
      setCreating(false);
    }
  };

  const handlePaste = async (e) => {
    const pastedText = e.clipboardData.getData('text');
    const lines = pastedText.split('\n').map((l) => l.trim()).filter(Boolean);

    if (lines.length <= 1) return; // single line - let browser paste normally

    e.preventDefault();
    setCreating(true);
    try {
      for (const line of lines) {
        const taskData = buildTaskData(line);
        const res = await tasksApi.create(taskData);
        const newTask = res.data;
        const bot = botOf(read(line));
        if (bot && newTask?.id) await botsApi.assignTask(newTask.id, bot);
        optimisticInsert(newTask);
        onTaskCreated?.(newTask);
      }
      setTitle('');
    } catch (err) {
      console.error('Failed to create tasks from paste:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (suggest.onKeyDown(e)) return;  // the "#" / "@" list has the keys while it is open
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate({ keepActive: true });
    } else if (e.key === 'Escape') {
      setTitle('');
      setIsActive(false);
      setChipMenu(null);
    }
  };

  // ── Chip menu (status / priority / due_date / duration / project) ────────
  // A single popover identified by `chipMenu.key` is rendered at a time. The
  // anchor rect is captured so the popover floats next to the chip the user
  // clicked, even if the row is scrolling.
  const [chipMenu, setChipMenu] = useState(null); // {key, rect} | null
  const chipMenuRef = useRef(null);

  const openChipMenu = (e, key) => {
    e.stopPropagation();
    e.preventDefault();
    if (chipMenu?.key === key) {
      setChipMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setChipMenu({ key, rect });
  };

  // Close on outside click / Escape. Also prevent the wrapper-level row click
  // from re-opening the menu.
  useEffect(() => {
    if (!chipMenu) return;
    const onMouseDown = (e) => {
      if (chipMenuRef.current && !chipMenuRef.current.contains(e.target)) {
        setChipMenu(null);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setChipMenu(null); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [chipMenu]);

  // Click-outside detector. Submits the typed title (or collapses the row)
  // when the user clicks fully outside both the inline row AND any open
  // chip popover. Used as the "blur to save" trigger - the native input
  // blur isn't enough because clicking a chip popover button focuses an
  // element that's outside the input's wrapper but still part of the
  // editing surface.
  // The latest handleCreate (it reads every chosen chip), so the listener needs no re-binding.
  const handleCreateRef = useRef(handleCreate);
  handleCreateRef.current = handleCreate;
  useEffect(() => {
    if (!isActive) return;
    const onDocMouseDown = (e) => {
      if (creating) return;
      const target = e.target;
      if (wrapperRef.current?.contains(target)) return;
      if (chipMenuRef.current?.contains(target)) return;
      // The date picker opens in a layer of its own (a portal): picking a day there is
      // part of editing the row, not a click away from it.
      if (target?.closest?.('[data-radix-popper-content-wrapper]')) return;
      if (title.trim()) {
        handleCreateRef.current({ keepActive: false });
      } else {
        setIsActive(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isActive, title, creating]);

  // ── Default-chip helpers ─────────────────────────────────────────────────
  const dueChipFor = (dateStr) => {
    if (!dateStr) return null;
    const date = dayOf(dateStr);  // the local day ("2026-11-04" alone is midnight UTC)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date < today) return { text: t('tasks.overdue'), className: 'text-red-400 bg-red-500/20' };
    if (date < tomorrow) return { text: t('tasks.today_label'), className: 'text-amber-700 dark:text-amber-400 bg-amber-500/20' };
    if (date < new Date(tomorrow.getTime() + 24 * 60 * 60 * 1000)) return { text: t('tasks.tomorrow_label'), className: 'text-blue-400 bg-blue-500/20' };
    return { text: formatDate(date, false), className: 'text-muted-foreground bg-gray-500/20' };
  };

  const currentStatus = useMemo(
    () => customStatuses.find((s) => s.id === chosenStatusId) || defaultStatus || null,
    [customStatuses, chosenStatusId, defaultStatus]
  );
  // What is typed wins over the row's own choices, live, in the row's own cells.
  const currentProject = useMemo(
    () => parsed.project || projects.find((p) => p.id === chosenProjectId) || null,
    [parsed.project, projects, chosenProjectId]
  );
  const shownDueDate = parsed.date ? toLocalDateString(parsed.date) : chosenDueDate;
  const shownDuration = parsed.duration || chosenDuration;
  const currentPriorityCfg = PRIORITIES[parsed.priority || chosenPriority] || PRIORITIES.p4;
  // The rest of the app (`priorityConfig` in TasksTable) shows altLabel
  // ("Low" / "Medium" / "High" / "Urgent") - mirror that here so the chip
  // text matches what the user sees on every other task row.
  const currentPriorityLabel = t(currentPriorityCfg.altLabelKey);
  const currentDueChip = dueChipFor(shownDueDate);
  const currentLabels = useMemo(
    () => [...availableLabels.filter((l) => chosenLabelIds.includes(l.id)),
      ...parsed.labels.filter((l) => !chosenLabelIds.includes(l.id))],
    [availableLabels, chosenLabelIds, parsed.labels]
  );
  const currentTaskType = useMemo(
    () => taskTypes.find((tt) => tt.id === chosenTaskTypeId) || null,
    [taskTypes, chosenTaskTypeId]
  );

  // ── Cell rendering for table-grid mode ──────────────────────────────────
  const baseWrap = {
    title:     'col-span-12 sm:col-span-4 flex items-center gap-3 min-w-0',
    status:    'hidden sm:flex col-span-2 items-center',
    priority:  'hidden sm:flex col-span-2 items-center',
    due_date:  'hidden sm:flex col-span-2 items-center',
    duration:  'hidden sm:flex col-span-2 items-center text-xs text-muted-foreground',
    project:   'hidden sm:flex col-span-2 items-center',
    labels:    'hidden sm:flex col-span-2 items-center gap-1',
    scheduled: 'hidden sm:flex col-span-2 items-center',
    task_type: 'hidden sm:flex col-span-2 items-center',
    assignee:  'hidden sm:flex col-span-2 items-center',
    owner:     'hidden sm:flex col-span-2 items-center',
    team:      'hidden sm:flex col-span-2 items-center',
    created_at:'hidden sm:flex col-span-2 items-center',
  };

  const renderGridCell = (key) => {
    const wrap = baseWrap[key] || 'hidden sm:flex col-span-2 items-center';

    if (key === 'title') {
      return (
        <div key={key} className={wrap}>
          <Plus className={`w-5 h-5 shrink-0 ${isActive ? 'text-amber-500' : 'text-muted-foreground'} transition-colors`} />
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            {...suggest.bind}
            placeholder={resolvedPlaceholder}
            className="flex-1 min-w-0 bg-transparent text-foreground text-sm placeholder:text-muted-foreground/50 outline-none py-0.5"
            disabled={creating}
          />
          {gridChips}
          {suggest.popup('app', { portal: true })}
          {creating && (
            <div className="w-3.5 h-3.5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin shrink-0" />
          )}
        </div>
      );
    }

    // Default chips only render once the user has actually started typing.
    // While the row is active but empty, the cells stay blank so the inline
    // row reads as an unobtrusive placeholder until the user commits to
    // creating a task.
    if (!isActive || !title.trim()) {
      return <div key={key} className={wrap} />;
    }

    if (key === 'status') {
      if (!currentStatus) return <div key={key} className={wrap} />;
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'status')}
            className="px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: `${currentStatus.color}30`, color: currentStatus.color }}
          >
            {statusName(currentStatus, t)}
          </button>
        </div>
      );
    }
    if (key === 'priority') {
      // Use altLabel ("Low" / "Medium" / "High" / "Urgent") so the chip text
      // matches every other task row in the app.
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'priority')}
            className={`px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity ${currentPriorityCfg.bgLight} ${currentPriorityCfg.textColor}`}
          >
            {currentPriorityLabel}
          </button>
        </div>
      );
    }
    if (key === 'due_date') {
      // The same date picker as the task's detail; the typing stays in the field meanwhile.
      return (
        <div key={key} className={wrap}>
          <DatePicker
            value={chosenDueDate}
            onChange={(date) => { setChosenDueDate(date ? toLocalDateString(date) : null); if (!date) setChosenDueTime(''); }}
            timeValue={chosenDueTime}
            onTimeChange={(v) => setChosenDueTime(v || '')}
            keepFocus
          >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            data-testid="inline-due-chip"
            className={
              currentDueChip
                ? `px-2 py-1 rounded text-xs font-medium hover:opacity-80 transition-opacity ${currentDueChip.className}`
                : 'flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground/80 transition-colors'
            }
          >
            {currentDueChip ? (
              currentDueChip.text
            ) : (
              <>
                <CalendarIcon className="w-3 h-3" />
                {t('tasks.no_date')}
              </>
            )}
          </button>
          </DatePicker>
        </div>
      );
    }
    if (key === 'duration') {
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'duration')}
            className="hover:text-foreground transition-colors"
          >
            {t('ui_task.duration.minutes', { m: shownDuration })}
          </button>
        </div>
      );
    }
    if (key === 'project') {
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'project')}
            className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity"
          >
            {currentProject ? (
              <>
                <span className="w-2 h-2 rounded shrink-0" style={{ backgroundColor: currentProject.color || '#6B7280' }} />
                <span className="text-xs text-foreground/80 truncate">{currentProject.name}</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground hover:text-foreground/80">-</span>
            )}
          </button>
        </div>
      );
    }
    if (key === 'labels') {
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'labels')}
            className="flex items-center gap-1 flex-wrap min-w-0 hover:opacity-80 transition-opacity"
          >
            {currentLabels.length > 0 ? (
              currentLabels.slice(0, 3).map((l) => (
                <span
                  key={l.id}
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  style={{ backgroundColor: `${l.color || '#6B7280'}30`, color: l.color || '#9CA3AF' }}
                >
                  {l.name}
                </span>
              ))
            ) : (
              <span className="text-xs text-muted-foreground hover:text-foreground/80">-</span>
            )}
            {currentLabels.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{currentLabels.length - 3}</span>
            )}
          </button>
        </div>
      );
    }
    if (key === 'task_type') {
      return (
        <div key={key} className={wrap}>
          <button
            type="button"
            onMouseDown={(e) => openChipMenu(e, 'task_type')}
            className="flex items-center gap-1.5 min-w-0 hover:opacity-80 transition-opacity"
          >
            {currentTaskType ? (
              <>
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: currentTaskType.color || '#6B7280' }}
                />
                <span className="text-xs text-foreground/80 truncate">{currentTaskType.name}</span>
              </>
            ) : (
              <span className="text-xs text-muted-foreground hover:text-foreground/80">-</span>
            )}
          </button>
        </div>
      );
    }
    // Auto-populated columns: assignee/owner default to the creator, team is
    // derived from the project, scheduled_start gets set by the planner, and
    // created_at + custom_fields are filled in after the task exists. Show a
    // dim "—" so the user understands these will be populated automatically.
    // "@person" typed: shown where the task's people show.
    if (key === 'assignee' && parsed.person) {
      return (
        <div key={key} className={wrap}>
          <span className="inline-flex items-center gap-1.5 min-w-0 text-xs text-foreground/80" data-testid="inline-task-person">
            {parsed.person.bot ? <BotAvatar bot={parsed.person.bot} size={20} /> : (
              <span className="w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] font-medium flex items-center justify-center flex-shrink-0">
                {parsed.personToken.charAt(0).toUpperCase()}
              </span>
            )}
            <span className="truncate">{parsed.personToken}</span>
          </span>
        </div>
      );
    }
    if (['assignee', 'owner', 'team', 'scheduled', 'created_at'].includes(key) || key.startsWith('cf_')) {
      return (
        <div key={key} className={wrap}>
          <span
            className="text-xs text-muted-foreground/70"
            title={t('tasks.inline_field_auto', {
              defaultValue: 'Set after the task is created',
            })}
          >
            -
          </span>
        </div>
      );
    }
    return <div key={key} className={wrap} />;
  };

  // ── Chip popover content ────────────────────────────────────────────────
  const renderChipMenu = () => {
    if (!chipMenu) return null;

    const baseDropdownStyle = (() => {
      const r = chipMenu.rect;
      const width =
        chipMenu.key === 'due_date' ? 280 :
        chipMenu.key === 'duration' ? 200 :
        chipMenu.key === 'project' ? 240 :
        chipMenu.key === 'labels' ? 220 :
        chipMenu.key === 'task_type' ? 220 :
        180;
      const height =
        chipMenu.key === 'due_date' ? 380 :
        chipMenu.key === 'project' ? 300 :
        chipMenu.key === 'labels' ? 280 :
        chipMenu.key === 'task_type' ? 280 :
        220;
      const spaceBelow = window.innerHeight - r.bottom - 8;
      const showAbove = spaceBelow < height && r.top > spaceBelow;
      const left = Math.min(r.left, window.innerWidth - width - 8);
      return showAbove
        ? { bottom: window.innerHeight - r.top + 6, left, width, maxHeight: r.top - 12, overflowY: 'auto' }
        : { top: r.bottom + 6, left, width, maxHeight: spaceBelow, overflowY: 'auto' };
    })();

    return (
      <div
        ref={chipMenuRef}
        className="fixed z-[60] bg-white dark:bg-popover border border-border dark:border-border rounded-xl shadow-2xl py-1"
        style={baseDropdownStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {chipMenu.key === 'status' && customStatuses
          .filter((s) => s.category !== 'archived')
          .map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { setChosenStatusId(s.id); setChipMenu(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                chosenStatusId === s.id
                  ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                  : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
              }`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
              {statusName(s, t)}
            </button>
          ))}

        {chipMenu.key === 'priority' && Object.entries(PRIORITIES).map(([key, cfg]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setChosenPriority(key); setChipMenu(null); }}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
              chosenPriority === key
                ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
            }`}
          >
            <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.bgColor}`} />
            {`${cfg.shortLabel} ${t(cfg.labelKey)}`}
          </button>
        ))}

        {chipMenu.key === 'duration' && (
          <div className="grid grid-cols-3 gap-1 p-2">
            {[15, 30, 45, 60, 90, 120, 180, 240, 480].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => { setChosenDuration(m); setChipMenu(null); }}
                className={`px-2 py-1.5 rounded text-xs font-medium transition-colors ${
                  chosenDuration === m
                    ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400'
                    : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                }`}
              >
                {m < 60
                  ? t('ui_task.duration.minutes', { m })
                  : t('ui_task.duration.hours', { h: m % 60 === 0 ? m / 60 : (m / 60).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) })}
              </button>
            ))}
          </div>
        )}

        {chipMenu.key === 'project' && (
          <>
            <button
              type="button"
              onClick={() => { setChosenProjectId(null); setChipMenu(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                !chosenProjectId
                  ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                  : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
              }`}
            >
              <span className="w-2 h-2 rounded shrink-0 bg-gray-500" />
              {t('common.no_project', { defaultValue: 'No project' })}
            </button>
            {projects.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setChosenProjectId(p.id); setChipMenu(null); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  chosenProjectId === p.id
                    ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                    : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                }`}
              >
                <span className="w-2 h-2 rounded shrink-0" style={{ backgroundColor: p.color || '#6B7280' }} />
                <span className="truncate">{p.name}</span>
              </button>
            ))}
          </>
        )}

        {chipMenu.key === 'labels' && (
          <>
            {availableLabels.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {t('tasks.no_labels_available', { defaultValue: 'No labels yet' })}
              </div>
            ) : (
              availableLabels.map((l) => {
                const selected = chosenLabelIds.includes(l.id);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      // Multi-select: toggle without closing the menu so the
                      // user can pick several labels in one go.
                      setChosenLabelIds((prev) =>
                        selected ? prev.filter((id) => id !== l.id) : [...prev, l.id]
                      );
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                      selected
                        ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                        : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                    }`}
                  >
                    <span
                      className="w-2 h-2 rounded shrink-0"
                      style={{ backgroundColor: l.color || '#6B7280' }}
                    />
                    <span className="truncate flex-1 text-left">{l.name}</span>
                    {selected && <span className="text-xs text-amber-500">✓</span>}
                  </button>
                );
              })
            )}
          </>
        )}

        {chipMenu.key === 'task_type' && (
          <>
            <button
              type="button"
              onClick={() => { setChosenTaskTypeId(null); setChipMenu(null); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                !chosenTaskTypeId
                  ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                  : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
              }`}
            >
              <span className="w-2 h-2 rounded-full shrink-0 bg-gray-500" />
              {t('tasks.no_task_type', { defaultValue: 'No type' })}
            </button>
            {taskTypes.map((tt) => (
              <button
                key={tt.id}
                type="button"
                onClick={() => { setChosenTaskTypeId(tt.id); setChipMenu(null); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                  chosenTaskTypeId === tt.id
                    ? 'bg-muted dark:bg-white/10 text-foreground dark:text-foreground font-medium'
                    : 'text-muted-foreground/70 dark:text-foreground/80 hover:bg-muted dark:hover:bg-white/5 hover:text-foreground dark:hover:text-foreground'
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: tt.color || '#6B7280' }}
                />
                <span className="truncate">{tt.name}</span>
              </button>
            ))}
          </>
        )}
      </div>
    );
  };

  // ── Render: table-grid mode ──────────────────────────────────────────────
  if (isTableLayout) {
    const visibleKeys = columnOrder.filter((k) => visibleColumns[k] !== false);
    const wrapperClass = [
      'group/inline grid gap-3 py-1.5 items-center transition-colors',
      isActive ? 'bg-amber-500/[0.04]' : 'cursor-pointer hover:bg-white/[0.03]',
      indented ? 'pl-10 pr-3' : 'px-3',
      'border-t border-border/50',
      className,
    ].join(' ');

    return (
      <>
        <div
          ref={wrapperRef}
          className={wrapperClass}
          style={{ gridTemplateColumns: gridTemplate }}
          onClick={() => { if (!isActive) setIsActive(true); }}
        >
          {leadingCell && <div />}
          {visibleKeys.map((key) => renderGridCell(key))}
          <div />
        </div>
        {renderChipMenu()}
      </>
    );
  }

  // ── Render: compact mode (KanbanBoard etc.) ──────────────────────────────
  if (!isActive) {
    return (
      <button
        onClick={() => setIsActive(true)}
        className={`w-full flex items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground hover:text-muted-foreground hover:bg-accent/60 transition-colors group ${className}`}
      >
        <Plus className="w-3 h-3 text-muted-foreground/70 group-hover:text-amber-500 transition-colors" />
        <span>{resolvedPlaceholder}</span>
      </button>
    );
  }

  return (
    <div className={`flex items-center gap-2 px-4 py-1 bg-accent/60 border-t border-border ${className}`}>
      <Plus className="w-3 h-3 text-amber-500 shrink-0" />
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        // Focus going into the date picker (its time list) does not close the empty row.
        onBlur={(e) => {
          if (e.relatedTarget?.closest?.('[data-radix-popper-content-wrapper]')) return;
          if (!creating && !title.trim()) setIsActive(false);
        }}
        {...suggest.bind}
        placeholder={`${resolvedPlaceholder} ${t('tasks.inline_hints')}`}
        className="flex-1 min-w-0 bg-transparent text-foreground text-xs placeholder:text-muted-foreground/70 outline-none"
        disabled={creating}
      />
      {liveChips}
      {suggest.popup('app', { portal: true })}
      {creating && (
        <div className="w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      )}
    </div>
  );
};

export default InlineTaskRow;
